"use client";

import {
  Flex,
  Heading,
  Text,
  Button as ChakraButton,
  Box,
} from "@chakra-ui/react";
import { Button, Button_T } from "@ui/components/button";
import { colors } from "@areahub/theme";
import { useAnalytics } from "@areahub/adapters/src/analytics/analytics.client";
import { useEffect, useState, useRef } from "react";
import Image from "next/image";

export interface VideoAsset {
  id: string;
  url: string;
}

export interface EnterpriseHeroProps {
  title: string;
  /**
   * Description text below the title
   */
  pushText: string;
  /**
   * CTA buttons
   */
  buttons: Button_T[];
  videoWebm?: VideoAsset;
  videoMp4?: VideoAsset;
  bg: string;
  textColor: string;
  titleColor: string;
}

export const EnterpriseHero = ({
  title,
  pushText,
  buttons,
  videoWebm,
  videoMp4,
  bg,
  textColor,
  titleColor,
}: EnterpriseHeroProps) => {
  const analytics = useAnalytics();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (reducedMotion) return;
    const start = () => {
      const el = videoRef.current;
      if (!el) return;
      el.muted = true;
      el.load();
      el.play().catch((error) => {
        if (error.name !== "NotAllowedError") console.error(el.error);
      });
    };
    const hasIdle =
      typeof window !== "undefined" && "requestIdleCallback" in window;
    const id = hasIdle
      ? window.requestIdleCallback(start)
      : window.setTimeout(start, 200);

    return () => {
      if (hasIdle) window.cancelIdleCallback(id);
      else window.clearTimeout(id);
    };
  }, [reducedMotion]);

  const scrollToSection = () => {
    const section = document.getElementById("clsaijhulj0gh0ak58spkyq15");
    section?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <Box bgColor={bg} overflow="hidden" position="relative">
      <Image
        src={
          "https://us-east-1.graphassets.com/Asv42xiViTEaNUIJrjCFhz/cmu1lixwn9o9t06k1m0gfa0dq"
        }
        alt=""
        fill
        priority
        sizes="100vw"
        style={{ objectFit: "cover", zIndex: 0 }}
      />
      {!reducedMotion && (
        <video
          ref={videoRef}
          muted
          loop
          playsInline
          disablePictureInPicture
          preload="none"
          aria-hidden="true"
          onCanPlay={() => setReady(true)}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            pointerEvents: "none",
            zIndex: 0,
            opacity: ready ? 1 : 0,
            transition: "opacity 400ms ease",
          }}
        >
          {videoWebm?.url && <source src={videoWebm.url} type="video/webm" />}
          {videoMp4?.url && <source src={videoMp4.url} type="video/mp4" />}
        </video>
      )}

      <Flex
        position="relative"
        zIndex={1}
        flexDir={{
          base: "column",
          lg: "row",
        }}
        pt={{
          base: 16,
          lg: 24,
        }}
        pb={{
          base: 12,
          lg: 20,
          xl: 12,
        }}
        px={{
          base: 10,
          xl: 0,
        }}
        overflow="visible"
        maxW="1215px"
        mx="auto"
        justifyContent={"space-between"}
      >
        <Flex flexDir="column">
          <Heading
            as={"h1"}
            fontSize={{
              base: 34,
              md: 40,
              lg: 46,
              xl: 50,
            }}
            textColor={titleColor}
            textShadow="0 2px 8px rgba(0, 0, 0, 0.6)"
            fontWeight={"bold"}
            lineHeight={1.2}
            pb={{
              base: 7,
              lg: 10,
            }}
            w={{
              md: "80%",
              lg: "60%",
            }}
          >
            {title}
          </Heading>
          <Text
            color={textColor}
            fontWeight={"bold"}
            fontSize={{
              base: 16,
              md: 18,
              lg: 20,
              xl: 22,
            }}
            pb={{
              base: 7,
              lg: 10,
            }}
            w={{
              lg: "75%",
              xl: "80%",
            }}
          >
            {pushText}
          </Text>
          <Flex
            flexDir={{
              base: "column",
              lg: "row",
            }}
            alignItems={{
              base: "center",
              lg: "unset",
            }}
            gap={{
              base: 4,
              lg: 5,
            }}
            pb={{
              base: 0,
              lg: 16,
            }}
          >
            <Button
              bgColor={"white"}
              color={colors.enterpriseColors.blue["300"]}
              fontSize={{
                base: 12,
                md: 14,
                lg: 16,
                xl: 18,
              }}
              fontWeight={"bold"}
              py={{
                base: "6",
                lg: "8",
              }}
              w={{
                base: "140px",
                md: "160px",
                lg: "180px",
                xl: "220px",
              }}
              onClick={() =>
                analytics.track(
                  "User clicked 'Request a Demo' button on enterprise landing page",
                  {
                    ui: "EnterpriseHero",
                  },
                )
              }
              textTransform={"uppercase"}
              {...buttons[0]}
            />
            <ChakraButton
              borderRadius={"10px"}
              bgColor={colors.enterpriseColors.blue["150"]}
              color="white"
              fontSize={{
                base: 12,
                md: 14,
                lg: 16,
                xl: 18,
              }}
              fontWeight={"bold"}
              py={{
                base: "6",
                lg: "8",
              }}
              w={{
                base: "140px",
                md: "160px",
                lg: "180px",
                xl: "220px",
              }}
              textTransform={"uppercase"}
              onClick={scrollToSection}
            >
              {buttons[1].label}
            </ChakraButton>
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
};
